import React, { useCallback } from 'react';
import categoryPageReferralIllustrationMobile from 'shef-web/src/assets/img/referral/category-page-referral-illustration-mobile.svg';
import categoryPageReferralIllustration from 'shef-web/src/assets/img/referral/category-page-referral-illustration.svg';
import { CategoryPageContent, ReferralStripTextColor } from 'src/gqlReactTypings.generated.d';
import { useReferralBanner } from 'src/pages/consumer/dish-first/explore/components/Banners/ReferralBanners';
import { DishFirstExploreWidthLimit } from 'src/pages/consumer/dish-first/explore/components/SharedStyles';
import { generateThumborImgUrl } from 'src/shared/design-system/DynamicImage';
import { VerticallyCenteredText } from 'src/shared/design-system/VerticallyCenteredText';
import { useHistory } from 'src/shared/hooks/useHistory';
import { minScreen } from 'src/shared/utils/StyledComponentUtils';
import styled from 'styled-components';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Border, Colors, Font, ScreenWidth, ScreenWidthValues, Spacing } from 'web-common/src/shared/styles';

const Container = styled(DishFirstExploreWidthLimit)`
  margin-top: ${Spacing.SINGLE_HALF};
  margin-bottom: ${Spacing.DOUBLE_HALF};

  ${minScreen(ScreenWidth.MEDIUM)} {
    padding: 0 16px 24px;
  }
`;

const Strip = styled.div<{ backgroundConfig: BackgroundConfig }>`
  display: flex;
  flex-direction: row;
  padding: 10px 16px;
  align-items: center;
  cursor: pointer;

  background: url(${({ backgroundConfig }) =>
      generateThumborImgUrl({
        src: backgroundConfig.referralStripBackgroundUrl,
        width: ScreenWidthValues[ScreenWidth.SMALL],
        height: 144,
      })})
    center / cover no-repeat;

  ${minScreen(ScreenWidth.SMALL)} {
    padding: 10px 40px;
    background: url(${({ backgroundConfig }) =>
        generateThumborImgUrl({
          src: backgroundConfig.referralStripBackgroundUrlTablet ?? backgroundConfig.referralStripBackgroundUrl,
          width: ScreenWidthValues[ScreenWidth.MEDIUM],
          height: 144,
        })})
      center / cover no-repeat;
  }

  ${minScreen(ScreenWidth.MEDIUM)} {
    border-radius: ${Border.Radius.M};
    background: url(${({ backgroundConfig }) =>
        generateThumborImgUrl({
          src: backgroundConfig.referralStripBackgroundUrlDesktop ?? backgroundConfig.referralStripBackgroundUrl,
          width: ScreenWidthValues[ScreenWidth.LARGE],
          height: 144,
        })})
      center / cover no-repeat;
  }
`;

const ReferralTitle = styled.div`
  display: flex;
  align-items: flex-start;
  width: fit-content;

  padding: ${Spacing.HALF};
  border-radius: ${Border.Radius.S};
  border: 1px solid ${Colors.JACKFRUIT};
  background: ${Colors.JACKFRUIT_LITE_20};

  color: ${Colors.GRAY_DARK_30};
  font-size: ${Font.Size.XXS};
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.SEMI_BOLD};
  line-height: 100%;
`;

const Explanation = styled.span<{ color: ReferralStripTextColor }>`
  color: ${({ color }) => (color === ReferralStripTextColor.Light ? Colors.WHITE : Colors.GRAY_DARK_30)};

  font-size: ${Font.Size.M};
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.BOLD};
`;

const Cta = styled.div<{ color: ReferralStripTextColor }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ color }) => (color === ReferralStripTextColor.Light ? Colors.WHITE : Colors.GRAY_DARK_30)};

  font-size: ${Font.Size.S};
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.MEDIUM};
`;

type BackgroundConfig = Pick<
  CategoryPageContent,
  | 'referralStripTextColor'
  | 'referralStripBackgroundUrl'
  | 'referralStripBackgroundUrlTablet'
  | 'referralStripBackgroundUrlDesktop'
>;

interface IProps {
  className?: string;
  backgroundConfig?: BackgroundConfig | null;
}

export const CategoryPageReferralStrip: React.FC<IProps> = ({ className, backgroundConfig }) => {
  const { loading, toUrl, referralCodeTitle, onClick } = useReferralBanner();
  const { isSmallScreen } = useScreenContext();
  const history = useHistory();

  const handleClick = useCallback(() => {
    onClick();
    history.push(toUrl);
  }, [history, onClick, toUrl]);

  if (!backgroundConfig || loading) return null;

  return (
    <Container className={className}>
      <Strip backgroundConfig={backgroundConfig} onClick={handleClick}>
        <img
          className='mr-6'
          src={isSmallScreen ? categoryPageReferralIllustrationMobile : categoryPageReferralIllustration}
        />
        <div className='flex flex-col'>
          <ReferralTitle className='mb-4'>{referralCodeTitle}</ReferralTitle>
          <Explanation className='mb-2' color={backgroundConfig.referralStripTextColor}>
            Earn unlimited credits for referring your friends
          </Explanation>
          <Cta color={backgroundConfig.referralStripTextColor}>
            <VerticallyCenteredText>Learn more</VerticallyCenteredText>
            <i className='fa fa-chevron-right ml-1 text-xs' />
          </Cta>
        </div>
      </Strip>
    </Container>
  );
};
